import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import {GroupDashboardService} from './group-dashboard.service';
import * as Actions$ from './group-dashboard.actions';
import {
  FetchDashboardFilterFailure,
  FetchDashboardFilterRequest,
  FetchDashboardFilterSuccess,
  FetchGroupsTypeFilterFailure,
  FetchGroupsTypeFilterRequest,
  FetchGroupsTypeFilterSuccess,
  FetchParticipantsAndCaregiversListFailure,
  FetchParticipantsAndCaregiversListRequest,
  FetchParticipantsAndCaregiversListSuccess,
  FetchParticipantsBehaviourListFailure,
  FetchParticipantsBehaviourListRequest,
  FetchParticipantsBehaviourListSuccess,
  FetchParticipantsWeightListFailure,
  FetchParticipantsWeightListRequest,
  FetchParticipantsWeightListSuccess,
  FetchUserStatusListFilterFailure,
  FetchUserStatusListFilterRequest,
  FetchUserStatusListFilterSuccess
} from './group-dashboard.actions';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {
  FetchGroupDashboardParticipantBehaviorPhysicalActivityModel,
  FetchGroupDashboardParticipantBehaviorRedLightsModel,
  FetchGroupDashboardParticipantBehaviorScreenTimeModel,
  FetchGroupDashboardParticipantInformationModel,
  FetchGroupDashboardParticipantWeightModel,
} from '../models/fetch-groups-dashboard.model';
import {Observable, of} from 'rxjs';
import {
  GroupDashboardParticipantInformationModel
} from '../../../../models/group-dashboard-participant-information.model';
import {
  GroupDashboardParticipantWeightModel
} from '../../../../models/group-dashboard-participant-weight.model';
import {
  GroupDashboardParticipantBehaviorRedLightsModel
} from '../../../../models/group-dashboard-participant-behavior-red-lights.model';
import {
  GroupDashboardParticipantBehaviorPhysicalActivityModel
} from 'src/app/models/group-dashboard-participant-behavior-physical-activity-model';
import {
  GroupDashboardParticipantBehaviorScreenTimeModel
} from 'src/app/models/group-dashboard-participant-behavior-screen-time-model';
import {
  FetchDashboardFilterModel
} from '../../clients-list/model/fetch-dashboard-filter.model';
import {
  FetchGroupsTypeFilterModel
} from '../../coach-groups-list/models/fetch-groups-type-filter.model';
import {
  FetchParticipantsBehaviourListModel
} from "../../clients-list/model/fetch-participants-behaviour-list.model";

@Injectable()
export class GroupDashboardEffects {
  fetchGroupDashboardParticipantInformation$: Observable<Action> = createEffect(
    () =>
      this.actions.pipe(
        ofType(
          Actions$.FetchGroupDashboardParticipantInformationDataRequest
        ),
        map((actions) => actions),
        mergeMap(() =>
          this.groupDashboardService
            .fetchGroupDashboardParticipantInformationData()
            .pipe(
              map(
                (
                  response: FetchGroupDashboardParticipantInformationModel['response']
                ) => {
                  const {data, message} = response;
                  const groupDashboardParticipantInformation: GroupDashboardParticipantInformationModel =
                    data;
                  return Actions$.FetchGroupDashboardParticipantInformationDataSuccess(
                    {
                      groupDashboardParticipantInformation:
                      groupDashboardParticipantInformation,
                      message: message,
                    }
                  );
                }
              ),
              catchError((error) => {
                return of(
                  Actions$.FetchGroupDashboardParticipantInformationDataFailure()
                );
              })
            )
        )
      )
  );

  fetchGroupDashboardParticipantWeight$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(Actions$.FetchGroupDashboardParticipantWeightDataRequest),
      map((actions) => actions),
      mergeMap(() =>
        this.groupDashboardService.fetchGroupDashboardParticipantWeightData()
          .pipe(
            map(
              (
                response: FetchGroupDashboardParticipantWeightModel['response']
              ) => {
                const {data, message} = response;
                const groupDashboardParticipantWeight: GroupDashboardParticipantWeightModel =
                  data;
                return Actions$.FetchGroupDashboardParticipantWeightDataSuccess(
                  {
                    groupDashboardParticipantWeight:
                    groupDashboardParticipantWeight,
                    message: message,
                  }
                );
              }
            ),
            catchError((error) => {
              return of(
                Actions$.FetchGroupDashboardParticipantInformationDataFailure()
              );
            })
          )
      )
    )
  );

  fetchGroupDashboardParticipantBehaviorRedLights$: Observable<Action> =
    createEffect(() =>
      this.actions.pipe(
        ofType(
          Actions$.FetchGroupDashboardParticipantBehaviorRedLightsDataRequest
        ),
        map((actions) => actions),
        mergeMap(() =>
          this.groupDashboardService
            .fetchGroupDashboardParticipantBehaviorRedLightsData()
            .pipe(
              map(
                (
                  response: FetchGroupDashboardParticipantBehaviorRedLightsModel['response']
                ) => {
                  const {data, message} = response;
                  const groupDashboardParticipantBehaviorRedLights: GroupDashboardParticipantBehaviorRedLightsModel =
                    data;
                  return Actions$.FetchGroupDashboardParticipantBehaviorRedLightsDataSuccess(
                    {
                      groupDashboardParticipantBehaviorRedLights:
                      groupDashboardParticipantBehaviorRedLights,
                      message: message,
                    }
                  );
                }
              ),
              catchError((error) => {
                return of(
                  Actions$.FetchGroupDashboardParticipantBehaviorRedLightsDataFailure()
                );
              })
            )
        )
      )
    );

  fetchGroupDashboardParticipantBehaviorPhysicalActivityData$: Observable<Action> =
    createEffect(() =>
      this.actions.pipe(
        ofType(
          Actions$.FetchGroupDashboardParticipantBehaviorPhysicalActivityDataRequest
        ),
        map((actions) => actions),
        mergeMap(() =>
          this.groupDashboardService
            .fetchGroupDashboardParticipantBehaviorPhysicalActivityData()
            .pipe(
              map(
                (
                  response: FetchGroupDashboardParticipantBehaviorPhysicalActivityModel['response']
                ) => {
                  const {data, message} = response;
                  const fetchGroupDashboardParticipantBehaviorPhysicalActivity: GroupDashboardParticipantBehaviorPhysicalActivityModel =
                    data;
                  return Actions$.FetchGroupDashboardParticipantBehaviorPhysicalActivityDataSuccess(
                    {
                      message: message,
                      groupDashboardParticipantBehaviorPhysicalActivity:
                      fetchGroupDashboardParticipantBehaviorPhysicalActivity,
                    }
                  );
                }
              ),
              catchError((error) => {
                return of(
                  Actions$.FetchGroupDashboardParticipantBehaviorPhysicalActivityDataFailure()
                );
              })
            )
        )
      )
    );

  fetchGroupDashboardParticipantBehaviorScreenTime$: Observable<Action> =
    createEffect(() =>
      this.actions.pipe(
        ofType(
          Actions$.FetchGroupDashboardParticipantBehaviorScreenTimeDataRequest
        ),
        map((actions) => actions),
        mergeMap(() =>
          this.groupDashboardService
            .fetchGroupDashboardParticipantBehaviorScreenTimeData()
            .pipe(
              map(
                (
                  response: FetchGroupDashboardParticipantBehaviorScreenTimeModel['response']
                ) => {
                  const {data, message} = response;
                  const groupDashboardParticipantBehaviorScreenTime: GroupDashboardParticipantBehaviorScreenTimeModel =
                    data;
                  return Actions$.FetchGroupDashboardParticipantBehaviorScreenTimeDataSuccess(
                    {
                      message: message,
                      groupDashboardParticipantBehaviorScreenTime:
                      groupDashboardParticipantBehaviorScreenTime,
                    }
                  );
                }
              ),
              catchError((error) => {
                return of(
                  Actions$.FetchGroupDashboardParticipantBehaviorScreenTimeDataFailure()
                );
              })
            )
        )
      )
    );

  fetchDashboardFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchDashboardFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.groupDashboardService.fetchDashboardFilter().pipe(
          map((response: FetchDashboardFilterModel['response']) => {
            const {data, message} = response;
            return FetchDashboardFilterSuccess({
              dashboardFilter: data,
              message
            });
          }),
          catchError((error) => {
            return of(FetchDashboardFilterFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchDashboardFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchDashboardFilterFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    )
  );

  fetchParticipantsAndCaregiversList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchParticipantsAndCaregiversListRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.groupDashboardService.fetchParticipantsAndCaregiversList(payload)
          .pipe(
            map((response) => {
              const {data, message} = response;
              return FetchParticipantsAndCaregiversListSuccess({
                participantsAndCaregiversList: data,
                message
              });
            }),
            catchError(() => {
              return of(FetchParticipantsAndCaregiversListFailure());
            }),
            tap((action: any) => {
              if (action.type === FetchParticipantsAndCaregiversListSuccess.type) {
                // Code to execute on API Success Action dispatch
              } else if (action.type === FetchParticipantsAndCaregiversListFailure.type) {
                // Code to execute on API Failure Action dispatch
              }
            })
          )
      )
    )
  );

  fetchGroupsTypeFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchGroupsTypeFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.groupDashboardService.fetchGroupsTypeFilter().pipe(
          map((response: FetchGroupsTypeFilterModel["response"]) => {
            const {data, message} = response;
            return FetchGroupsTypeFilterSuccess({
              groupsTypeFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchGroupsTypeFilterFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchGroupsTypeFilterSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchGroupsTypeFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchUserStatusListFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchUserStatusListFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.groupDashboardService.fetchUserStatusListFilter().pipe(
          map((response) => {
            const {data, message} = response;
            return FetchUserStatusListFilterSuccess({
              data,
              message
            });
          }),
          catchError((error) => {
            return of(FetchUserStatusListFilterFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchUserStatusListFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchUserStatusListFilterFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    )
  );

  fetchParticipantsWeightList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchParticipantsWeightListRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.groupDashboardService.fetchParticipantsWeightList(payload).pipe(
          map((response) => {
            const {data, message} = response;
            return FetchParticipantsWeightListSuccess({
              participantsWeightList: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchParticipantsWeightListFailure());
          }),
          tap((action) => {
            if (action.type === FetchParticipantsWeightListSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchParticipantsWeightListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchParticipantsBehaviourList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchParticipantsBehaviourListRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchParticipantsBehaviourListModel['request']) =>
        this.groupDashboardService.fetchParticipantsBehaviourList(payload)
          .pipe(
            map((response: FetchParticipantsBehaviourListModel['response']) => {
              const {data, message} = response;
              return FetchParticipantsBehaviourListSuccess({
                participantsBehaviourList: data,
                message
              });
            }),
            catchError(() => {
              return of(FetchParticipantsBehaviourListFailure());
            }),
            tap((action: any) => {
              if (action.type === FetchParticipantsBehaviourListSuccess.type) {
                // Code to execute on API Success Action dispatch
              } else if (action.type === FetchParticipantsBehaviourListFailure.type) {
                // Code to execute on API Failure Action dispatch
              }
            })
          )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private groupDashboardService: GroupDashboardService,
    private actions: Actions
  ) {
  }
}
